import { useRef, useEffect } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { OriginCore3D } from './OriginCore3D'
import { CalendarCheck2, Search, CreditCard, Users, Palette, MessageSquare } from 'lucide-react'

gsap.registerPlugin(ScrollTrigger)

const steel = {
  ink: '#24313f',
  line: '#526a7e',
  mist: '#dbe4ec',
  wash: '#edf2f6',
} as const

const palette = {
  primary: '#005ea1',
  muted: '#3d4550',
  surfaceLow: '#f2f4f6',
} as const

const ecosystemNodes = [
  { icon: Search, label: 'Look-to-Book', desc: 'Visual search → booking' },
  { icon: CalendarCheck2, label: 'Smart Scheduling', desc: 'AI-optimized calendar' },
  { icon: CreditCard, label: 'Revenue Ops', desc: 'Stripe-native payouts' },
  { icon: Users, label: 'Client DNA', desc: 'Full history + alerts' },
  { icon: Palette, label: 'Theme Engine', desc: 'Brand-grade design' },
  { icon: MessageSquare, label: 'Messaging', desc: 'Automated outreach' },
] as const

export default function OriginEcosystemSection() {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const ctx = gsap.context(() => {
      const nodes = gsap.utils.toArray<HTMLElement>('.eco-node')
      gsap.fromTo(
        nodes,
        { opacity: 0, y: 40, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.12,
          scrollTrigger: {
            trigger: el,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      )

      gsap.fromTo(
        '.eco-heading',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 75%',
            toggleActions: 'play none none reverse',
          },
        }
      )
    }, el)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={containerRef}
      className="relative overflow-hidden px-6 py-24 lg:px-16 xl:px-24"
      style={{
        background: 'transparent',
      }}
    >
      {/* OriginCore3D — branded wireframe orb as sub-backdrop, now more subtle */}
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.15 }}>
        <OriginCore3D />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto">
        <div className="text-center mb-16">
          <p
            className="eco-heading mb-3 text-xs font-semibold uppercase tracking-[0.2em]"
            style={{ color: palette.primary }}
          >
            One platform, every signal
          </p>
          <h2
            className="eco-heading text-4xl font-bold tracking-tight md:text-5xl"
            style={{ fontFamily: 'Cormorant Garamond, serif', color: steel.ink }}
          >
            The Origin Ecosystem
          </h2>
          <p className="eco-heading mt-4 text-lg max-w-xl mx-auto" style={{ color: palette.muted }}>
            Six interconnected modules that turn bookings into intelligence and intelligence into
            revenue.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ecosystemNodes.map((node) => (
            <div
              key={node.label}
              className="eco-node group relative rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              style={{
                background: 'rgba(255,255,255,0.85)',
                borderColor: 'rgba(143,166,186,0.30)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <div
                className="absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                style={{
                  background: `linear-gradient(135deg, rgba(0,94,161,0.04) 0%, transparent 60%)`,
                }}
              />
              <div className="relative z-10">
                <div
                  className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl"
                  style={{
                    background: 'rgba(0,94,161,0.08)',
                    color: palette.primary,
                  }}
                >
                  <node.icon className="h-5 w-5" />
                </div>
                <h3 className="mb-1 text-base font-bold" style={{ color: steel.ink }}>
                  {node.label}
                </h3>
                <p className="text-sm" style={{ color: palette.muted }}>
                  {node.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
